/**
 * cdc-boost-card.js
 *
 * Hydrates Boost-rendered product cards with metaobject / product-reference
 * data that Boost cannot expose cleanly in templates.
 *
 * Two data sources, merged:
 *
 *  A. Boost product.metafields (server-side, in product-info.liquid)
 *     Emitted as data attributes on .cdc-card-info:
 *       data-category-raw     → JSON-stringified metaobject_reference
 *       data-cdc-collection-raw → JSON-stringified metaobject_reference
 *       data-visual-swatch-raw  → JSON-stringified list of product GIDs (no URL/image)
 *
 *  B. window.__cdcCardData (server-side, from snippets/cdc-card-metaobject-data.liquid)
 *     Keyed by product.id. Has fully-resolved swatch URL + image because rendered
 *     by Shopify Liquid which can iterate product references. Seeds the first 50
 *     products inline; the rest are filled by paging the resolver section in the
 *     current collection/search context (see fetchResolverData).
 *
 * After every boostPLPProductsLoaded (and on DOM mutations):
 *   1. For each .boost-sd__product-item:
 *      - Parse metaobject JSON from data attrs (A) → fill badge + category slot
 *      - Look up __cdcCardData (B) → fill swatch slot (B has image URLs A lacks)
 *      - If not in the map yet → trigger the one-shot resolver paging
 *   2. Re-init xBadges + Lipscore for new DOM.
 */
(function () {
  'use strict';

  // Shopify Liquid has NO access to arbitrary query-string params (the `request` object
  // exposes no `params`), so we cannot pass a handle list to the resolver section. Instead
  // we fetch the resolver in the current COLLECTION/SEARCH context — the Section Rendering
  // API resolves `collection`/`search` from the URL path — and it renders swatch data for
  // every product in that context. `page` is the one URL param Liquid honours (pagination),
  // so we page through with paginate-by-250 until a short page signals the end.
  const RESOLVER_SECTION = 'cdc-card-metaobject-resolver';
  const RESOLVER_PAGE_SIZE = 250; // must match `paginate ... by` in the resolver section
  const RESOLVER_MAX_PAGES = 40;  // safety cap (40 * 250 = 10k products)
  let resolverFetchStarted = false;

  // ---------- DOM helpers ----------

  function getProductId(item) {
    return item.getAttribute('data-product-id') || item.id || null;
  }

  function getInfoEl(item) {
    return item.querySelector('.cdc-card-info');
  }

  function safeJsonParse(str) {
    if (!str) return null;
    try { return JSON.parse(str); } catch (e) { return null; }
  }

  function escapeAttr(s) {
    if (!s) return '';
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  // ---------- Metaobject parsing ----------

  /**
   * Boost emits metaobject_reference values as JSON like:
   *   [{ id, data: { fields: [{ key, type, jsonValue, value, ... }] } }]
   * We want the `value` of the field whose key matches the requested label key.
   */
  function extractMetaobjectField(raw, fieldKey) {
    const parsed = safeJsonParse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const fields = parsed[0]?.data?.fields;
    if (!Array.isArray(fields)) return null;
    const match = fields.find(f => f && f.key === fieldKey);
    return match ? (match.value || match.jsonValue || null) : null;
  }

  function extractCategoryLabel(infoEl) {
    return extractMetaobjectField(infoEl.dataset.categoryRaw, 'label');
  }

  function extractCdcCollectionTitle(infoEl) {
    return extractMetaobjectField(infoEl.dataset.cdcCollectionRaw, 'title');
  }

  // ---------- Injection ----------

  function injectFromBoostData(item) {
    const infoEl = getInfoEl(item);
    if (!infoEl) return;

    // Category line (Maillot Super Léger Femme / Women's Superlight Jersey)
    const categorySlot = item.querySelector('[data-cdc-slot="category"]');
    if (categorySlot && !categorySlot.dataset.cdcFilled) {
      const label = extractCategoryLabel(infoEl);
      if (label) {
        categorySlot.textContent = label;
        categorySlot.removeAttribute('hidden');
        categorySlot.dataset.cdcFilled = '1';
      }
    }

    // CLASSIC / RACE / AUDAX badge
    const badgeSlot = item.querySelector('[data-cdc-slot="badge"]');
    if (badgeSlot && !badgeSlot.dataset.cdcFilled) {
      const title = extractCdcCollectionTitle(infoEl);
      if (title) {
        badgeSlot.textContent = title;
        badgeSlot.removeAttribute('hidden');
        badgeSlot.dataset.cdcFilled = '1';
      }
    }
  }

  function injectFromMap(item, data) {
    if (!item || !data) return;

    // Fallback for category (if Boost didn't expose it)
    const categorySlot = item.querySelector('[data-cdc-slot="category"]');
    if (categorySlot && !categorySlot.dataset.cdcFilled && data.categoryLabel) {
      categorySlot.textContent = data.categoryLabel;
      categorySlot.removeAttribute('hidden');
      categorySlot.dataset.cdcFilled = '1';
    }

    // Fallback for badge
    const badgeSlot = item.querySelector('[data-cdc-slot="badge"]');
    if (badgeSlot && !badgeSlot.dataset.cdcFilled && data.cdcCollection) {
      badgeSlot.textContent = data.cdcCollection;
      badgeSlot.removeAttribute('hidden');
      badgeSlot.dataset.cdcFilled = '1';
    }

    // Visual swatches — only __cdcCardData has URL+image (Boost gives only GIDs)
    const swatchSlot = item.querySelector('[data-cdc-slot="swatches"]');
    if (swatchSlot && !swatchSlot.dataset.cdcFilled && Array.isArray(data.visualSwatches) && data.visualSwatches.length > 0) {
      swatchSlot.innerHTML = data.visualSwatches
        .map(s =>
          '<a href="' + escapeAttr(s.url) + '" class="cdc-card-swatch">' +
          '<img loading="lazy" src="' + escapeAttr(s.image) + '" alt="' + escapeAttr(s.alt || '') + '" width="500" height="700">' +
          '</a>'
        )
        .join('');
      swatchSlot.removeAttribute('hidden');
      swatchSlot.dataset.cdcFilled = '1';
    }
  }

  // ---------- Resolver fetch (for products beyond the inline first 50) ----------

  // Build the resolver URL in the CURRENT context. /collections/{handle} gives the
  // resolver a `collection`; /search gives it `search` (q + type must be preserved).
  // Boost's own filter/sort params are left off — we want every product's data, unfiltered.
  function buildResolverUrl(page) {
    const loc = window.location;
    const isSearch = /(^|\/)search$/.test(loc.pathname);
    let qs = 'section_id=' + RESOLVER_SECTION + '&page=' + page;
    if (isSearch) {
      const q = new URLSearchParams(loc.search).get('q');
      if (q) qs += '&type=product&q=' + encodeURIComponent(q);
    }
    return loc.pathname + '?' + qs;
  }

  function onResolverContext() {
    const path = window.location.pathname;
    return /(^|\/)collections\/[^/]+/.test(path) || /(^|\/)search$/.test(path);
  }

  // Fetch one resolver page; merge its data and return how many products it contained.
  function fetchResolverPage(page) {
    return fetch(buildResolverUrl(page), { credentials: 'same-origin' })
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const payload = doc.getElementById('cdc-resolver-payload');
        if (!payload) return 0;
        const data = safeJsonParse(payload.textContent);
        if (!data) return 0;
        window.__cdcCardData = window.__cdcCardData || {};
        Object.assign(window.__cdcCardData, data);
        processAllItems();
        return Object.keys(data).length;
      });
  }

  // Page through the whole collection/search context once. A page shorter than
  // RESOLVER_PAGE_SIZE is the last one. Also stops if a page adds no new products —
  // a guard in case the Section Rendering API ignores `page` (then page 1 repeats).
  // Runs at most once per page load.
  async function fetchResolverData() {
    if (resolverFetchStarted || !onResolverContext()) return;
    resolverFetchStarted = true;
    for (let page = 1; page <= RESOLVER_MAX_PAGES; page++) {
      const before = Object.keys(window.__cdcCardData || {}).length;
      let count = 0;
      try {
        count = await fetchResolverPage(page);
      } catch (err) {
        console.warn('[cdc-boost-card] resolver fetch failed', err);
        break;
      }
      const added = Object.keys(window.__cdcCardData || {}).length - before;
      if (count < RESOLVER_PAGE_SIZE || added === 0) break;
    }
  }

  // ---------- Per-item processing ----------

  function processItem(item) {
    if (!item) return;
    injectFromBoostData(item);

    const id = getProductId(item);
    if (!id) return;
    const mapData = window.__cdcCardData && window.__cdcCardData[id];
    if (mapData) {
      injectFromMap(item, mapData);
    } else {
      // Not in the map yet — its data arrives once fetchResolverData() pages through the
      // collection/search context. Kick that off (no-op if already running/done).
      fetchResolverData();
    }
  }

  function processAllItems() {
    document.querySelectorAll('.boost-sd__product-item').forEach(processItem);
  }

  // ---------- Re-init hooks ----------

  function reinitAlpine() {
    if (window.Alpine && typeof window.Alpine.initTree === 'function') {
      try {
        const root = document.querySelector('.boost-sd__product-list') || document.body;
        window.Alpine.initTree(root);
      } catch (e) { /* noop */ }
    }
    const badgeTargets = document.querySelectorAll('.boost-sd__product-item [x-labels-data]:not([data-cdc-badged])');
    badgeTargets.forEach(el => {
      el.setAttribute('data-cdc-badged', '1');
      if (window.Alpine?.store?.('xBadges')) {
        try { window.Alpine.store('xBadges').load(el); } catch (e) { /* noop */ }
      }
    });
  }

  function reinitLipscore() {
    if (window.lipscore && typeof window.lipscore.initWidgets === 'function') {
      try { window.lipscore.initWidgets(); } catch (e) { /* noop */ }
    }
  }

  function reinitOutsidersGate() {
    const assets = window.__cdcAssets || {};

    // Repair the Outsiders Club logos — Boost's liquid can't resolve asset_url, so the
    // server-rendered src is broken. Re-point them at the real theme asset URLs.
    if (assets.outsidersLogoBlack) {
      document.querySelectorAll('.cdc-card-outsiders-logo').forEach(img => {
        if (img.getAttribute('src') !== assets.outsidersLogoBlack) img.src = assets.outsidersLogoBlack;
      });
    }

    // Reveal gate overlay only for guests. window.__cdcCustomerId is set by theme.liquid.
    const isGuest = !window.__cdcCustomerId;
    document.querySelectorAll('.cdc-card-outsiders-gate[data-outsiders-gate]').forEach(el => {
      const img = el.querySelector('img');
      if (img && assets.outsidersLogoWhite && img.getAttribute('src') !== assets.outsidersLogoWhite) {
        img.src = assets.outsidersLogoWhite;
      }
      if (isGuest) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  // ---------- Event wiring ----------

  function handleProductsLoaded() {
    requestAnimationFrame(() => {
      // Isolate each step so one throwing doesn't skip the others (notably the
      // Outsiders logo repair, which used to be last and silently skipped).
      try { processAllItems(); } catch (e) { /* noop */ }
      try { reinitAlpine(); } catch (e) { /* noop */ }
      try { reinitLipscore(); } catch (e) { /* noop */ }
      try { reinitOutsidersGate(); } catch (e) { /* noop */ }
    });
  }

  document.addEventListener('boostPLPProductsLoaded', handleProductsLoaded);
  document.addEventListener('boostSDAppLoaded', handleProductsLoaded);
  document.addEventListener('cdc:card-data-ready', handleProductsLoaded);

  // ---------- Card detection (observer is primary; Boost may not fire events on initial render) ----------
  let mutationDebounce = null;
  function scheduleProcess() {
    if (mutationDebounce) clearTimeout(mutationDebounce);
    mutationDebounce = setTimeout(() => {
      // Always repair Outsiders logos / sync the gate — Boost may inject the logo <img>
      // AFTER an item is marked processed, so this must run even with no unprocessed items.
      try { reinitOutsidersGate(); } catch (e) { /* noop */ }

      const unprocessed = document.querySelectorAll(
        '.boost-sd__product-item:not([data-cdc-processed])'
      );
      if (unprocessed.length === 0) return;
      unprocessed.forEach(item => item.setAttribute('data-cdc-processed', '1'));
      handleProductsLoaded();
    }, 80);
  }

  function startWatching() {
    new MutationObserver(scheduleProcess).observe(document.body, {
      childList: true,
      subtree: true,
    });
    scheduleProcess();
    // Proactively page the full collection/search context so swatch data for products
    // beyond the inline first 50 is ready before the shopper scrolls to them.
    fetchResolverData();
  }

  // Boost delegates clicks inside .boost-sd__product-item and navigates to the base
  // product URL — that strips the ?variant param off our size links and overrides the
  // swatch links (which point to OTHER variant products). Intercept these clicks in
  // the capture phase, stop Boost's handler, and follow the link's own href.
  function setupCardLinkNavigation() {
    document.addEventListener('click', function (event) {
      const link = event.target.closest('.cdc-card-size, .cdc-card-swatch');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href) return;
      event.stopImmediatePropagation();
      event.preventDefault();
      window.location.href = href;
    }, true);
  }

  setupCardLinkNavigation();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWatching);
  } else {
    startWatching();
  }
})();
